import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Compass, Truck, Workflow, MessagesSquare, Layers, BookOpen, Sparkles,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import SeoHead from "../components/SeoHead";

const PHILOSOPHY = [
  {
    title: "Systems over personalities.",
    body: "Durable businesses run on discipline, not on the memory or availability of any one person — including the owner.",
  },
  {
    title: "Knowledge as a business asset.",
    body: "The expertise that makes a company work belongs in playbooks, workflows, and dashboards — not in the heads of a few key people.",
  },
  {
    title: "Practical execution over marketing hype.",
    body: "We measure ourselves on installed change, not decks. The work is not done until the system holds without us.",
  },
  {
    title: "AI as an accelerator, not the product.",
    body: "AI compresses research and pattern-work so leaders can decide faster. It never replaces human judgement or accountability.",
  },
  {
    title: "Repeatability and operational excellence.",
    body: "If it cannot be repeated by a capable team, it is a heroic act — and heroics do not scale.",
  },
];

const EXPERTISE = [
  { icon: Compass, label: "Operations Leadership" },
  { icon: Truck, label: "Supply Chain & Logistics" },
  { icon: Workflow, label: "Process Improvement" },
  { icon: MessagesSquare, label: "Executive Communication" },
  { icon: Layers, label: "Organizational Systems" },
  { icon: BookOpen, label: "Knowledge Management" },
  { icon: Sparkles, label: "AI Workflow Design" },
];

export default function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== "#founder") return;
    const attempt = (tries = 0) => {
      const el = document.querySelector('[data-testid="meet-the-founder"]');
      if (!el) {
        if (tries < 10) setTimeout(() => attempt(tries + 1), 120);
        return;
      }
      if (window.__lenis) {
        window.__lenis.scrollTo(el, { offset: -40 });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    // let the page mount + Lenis warm up
    setTimeout(() => attempt(), 250);
  }, [hash]);

  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Jay Bailey | Local Proof STL",
    "url": "https://localproofstl.com/about",
    "mainEntity": {
      "@type": "Person",
      "name": "Jay Bailey",
      "jobTitle": "Founder",
      "worksFor": { "@type": "Organization", "name": "Local Proof STL" },
      "knowsAbout": [
        "Operations Leadership",
        "Supply Chain & Logistics",
        "Process Improvement",
        "Executive Communication",
        "Organizational Systems",
        "Knowledge Management",
        "AI Workflow Design",
      ],
    },
  };

  return (
    <div data-testid="about-page" className="pt-[72px]">
      <SeoHead
        title="About Jay Bailey | Local Proof STL"
        description="Meet Jay Bailey and the philosophy behind Local Proof STL — converting operational expertise into repeatable business assets that outlast individuals."
        canonical="https://localproofstl.com/about"
        ogType="profile"
        ogUrl="https://localproofstl.com/about"
        ogImage="https://localproofstl.com/og-image.jpg"
        twitterImage="https://localproofstl.com/og-image.jpg"
        jsonLd={aboutJsonLd}
        jsonLdId="about-jsonld"
      />
      {/* HERO */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
        <Reveal>
          <p className="eyebrow mb-8">About Local Proof STL</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
            We believe businesses should scale through discipline &mdash; not dependence.
          </h1>
        </Reveal>
      </section>

      {/* OUR STORY */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-24 lg:pb-32" data-testid="about-story">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">Our story</p>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg text-charcoal/80 leading-relaxed">
            <p>
              Local Proof STL was founded on a simple observation: the businesses that scale are
              not the ones with the best ideas &mdash; they are the ones with the best execution.
              Owner-led service companies are full of talented operators who have simply outgrown
              the systems that got them here.
            </p>
            <p>
              We are an executive operations firm. We do not sell frameworks or leave binders behind.
              We assess honestly, prioritize ruthlessly, and &mdash; when invited &mdash; roll up our
              sleeves to install the operating systems that let a business run without its owner in
              every decision.
            </p>
            <p>
              We work with owner-led service businesses because they reward operational excellence
              more than almost any other model. The disciplines we install &mdash; scheduling,
              quality execution, recurring revenue, leadership cadence &mdash; translate across
              every trade and service line.
            </p>
          </div>
        </div>
      </section>

      {/* WHY WE BUILT LOCAL PROOF STL */}
      <section className="bg-surface border-y border-border" data-testid="why-we-built">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-6">Why we built Local Proof STL</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-ink">
                Most operational knowledge is never captured.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-6 text-lg text-charcoal/80 leading-relaxed">
              <p>
                Inside every established service business is a body of expertise that took years to
                earn &mdash; how the schedule really works, how the hardest customers are handled,
                how a good technician is trained, how a job stays on margin. Almost none of it is
                written down.
              </p>
              <p>
                When that knowledge lives only in people, the business becomes fragile. Growth stalls
                the moment a key person is stretched, distracted, or gone. Quality varies with who
                happens to be on the job. Owners can never step back, because the operating system
                is inside their head.
              </p>
              <p>
                Local Proof STL exists to reverse that pattern. We convert operational expertise into
                durable business assets &mdash; playbooks, workflows, dashboards, and cadence &mdash;
                so what leaders already know becomes something the enterprise owns and can repeat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY QUOTE — visual break */}
      <section className="bg-ink text-white relative overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <p className="eyebrow text-mist mb-6">Our operating belief</p>
          <blockquote className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight max-w-4xl">
            &ldquo;The goal is not to remove the owner from the business. It&rsquo;s to remove the
            business&rsquo;s fragile dependence on the owner &mdash; so they can finally lead it.&rdquo;
          </blockquote>
        </div>
      </section>

      {/* OUR PHILOSOPHY — 5 principles */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="our-philosophy">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-6">Our philosophy</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight text-ink">
              Five principles that shape how we work.
            </h2>
          </div>
        </div>
        <div className="border-t border-border">
          {PHILOSOPHY.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-4 md:gap-10 py-10 border-b border-border group">
                <div className="md:col-span-1 font-serif text-2xl text-steel tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="md:col-span-5 font-serif text-2xl lg:text-3xl text-ink leading-snug">
                  {p.title}
                </h3>
                <p className="md:col-span-6 text-charcoal/70 text-[15px] leading-relaxed self-center">
                  {p.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LEADERSHIP EXPERIENCE — compact expertise panel */}
      <section className="bg-surface border-y border-border" data-testid="leadership-experience">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-28">
          <Reveal className="max-w-2xl mb-12">
            <p className="eyebrow mb-6">Leadership experience</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight text-ink">
              The disciplines we bring to every engagement.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-2xl overflow-hidden">
            {EXPERTISE.map((e, i) => (
              <Reveal key={e.label} delay={i * 0.04}>
                <div className="bg-white p-7 h-full hover:bg-cloud/20 transition-colors duration-300 flex flex-col justify-between min-h-[150px]">
                  <e.icon size={22} className="text-steel" strokeWidth={1.5} />
                  <p className="mt-8 font-serif text-xl text-ink leading-snug">{e.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEET THE FOUNDER — compact, deliberately small trust signal */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32" data-testid="meet-the-founder">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left column: portrait + name/title */}
            <div className="lg:col-span-4">
              <div
                data-testid="founder-headshot"
                className="w-[150px] h-[150px] lg:w-[176px] lg:h-[176px] rounded-full overflow-hidden bg-surface border border-border shadow-[0_20px_50px_-30px_rgba(11,31,51,0.35)]"
              >
                <img
                  src="/jay-bailey.jpg"
                  alt="Jay Bailey, Founder of Local Proof STL"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-8">
                <p className="eyebrow mb-3">Founder</p>
                <h2 className="font-serif font-light text-4xl lg:text-5xl leading-[1.02] tracking-tight text-ink">
                  Meet Jay Bailey
                </h2>
                <p className="mt-3 text-steel text-sm tracking-wide">
                  Founder <span className="mx-1.5 text-cloud">|</span> Local Proof STL
                </p>
              </div>
            </div>

            {/* Right column: refined biography + closing pull quote */}
            <div className="lg:col-span-7 lg:col-start-6 lg:pt-2">
              <div className="space-y-5 text-[17px] leading-[1.75] text-charcoal/85">
                <p>
                  Jay Bailey has built his career leading complex operations, logistics, and
                  organizational improvement inside owner-led and enterprise businesses. Across
                  every environment, the same pattern held: the expertise that made a company work
                  lived inside a handful of people &mdash; and left with them when they were
                  stretched, distracted, or gone.
                </p>
                <p>
                  Local Proof STL was built to reverse that pattern. The firm exists to convert
                  operational expertise into reusable business assets &mdash; the playbooks,
                  workflows, dashboards, and cadence that lift sales, tighten operations, shorten
                  onboarding, and compound the long-term value of the enterprise.
                </p>
                <p>
                  The work is deliberately practical. We favor installed systems over marketing
                  hype, and we treat AI as an accelerator that compresses research and pattern-work
                  &mdash; never as the product itself. What clients keep, in the end, is a business
                  that knows more than any single person inside it.
                </p>
              </div>

              <div className="mt-10 pl-6 border-l border-cloud" data-testid="founder-quote">
                <p className="font-serif italic text-lg lg:text-xl text-ink/85 leading-relaxed">
                  &ldquo;Great businesses aren&rsquo;t built on what one person knows. They&rsquo;re
                  built on systems that allow knowledge to outlast the individual.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
